import { useShallow } from "zustand/shallow";

import useRowKeyboardNavigation from "../Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import TableBodyRow from "../Table/Row";
import { TableCellPerColumn, TableSimpleDataItem, TableSimpleStore } from "./types";
import { selectVisibleColumns } from "../Table/hooks/useTableStore/selectors";
import { isStickyLeft, isStickyRight } from "./helpers";

type TableSimpleRowsProps<T extends TableSimpleDataItem> = {
  data: T[];
  store: TableSimpleStore;
  cellPerColumn: TableCellPerColumn<T>;
};

const TableSimpleRows = <T extends TableSimpleDataItem>({
  data,
  store,
  cellPerColumn,
}: TableSimpleRowsProps<T>) => {
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });
  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  return data.map((item) => (
    <TableBodyRow key={item.id} {...keyboardProps}>
      {columns.map((column, i) => {
        const cellId = `${item.id}-${column.id}`;
        const Cell = cellPerColumn[column.id];
        const stickyLeft = isStickyLeft(i, isFirstColumnPinned);
        const stickyRight = isStickyRight(i, columns.length);

        return (
          <Cell
            key={cellId}
            id={cellId}
            stickyLeft={stickyLeft}
            stickyRight={stickyRight}
            item={item}
          />
        );
      })}
    </TableBodyRow>
  ));
};

TableSimpleRows.displayName = "DS.TableSimple.Rows";

export default TableSimpleRows;
